const parseTimestamp = (timestamp) => {
	const parts = timestamp.split(':').map(Number);

	if (parts.length === 3) {
		return (parts[0] * 3600) + (parts[1] * 60) + parts[2];
	}

	return (parts[0] * 60) + parts[1];
};

const decodeCueText = (text) => {
	const decoder = document.createElement('textarea');
	decoder.innerHTML = text.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, '');

	return decoder.value.replace(/\s+/g, ' ').trim();
};

export const parseWebVtt = (content) => {
	const normalizedContent = content.replace(/^\uFEFF/, '').replace(/\r/g, '');
	const blocks = normalizedContent.split(/\n{2,}/);
	const timingPattern = /^((?:\d{2}:)?\d{2}:\d{2}\.\d{3})\s+-->\s+((?:\d{2}:)?\d{2}:\d{2}\.\d{3})(?:\s+.*)?$/;

	return blocks.reduce((cues, block) => {
		const lines = block.split('\n').map((line) => line.trim()).filter(Boolean);

		if (!lines.length || /^(WEBVTT|NOTE|STYLE|REGION)/.test(lines[0])) {
			return cues;
		}

		const timingIndex = lines.findIndex((line) => timingPattern.test(line));
		if (timingIndex === -1) return cues;

		const timing = lines[timingIndex].match(timingPattern);
		const text = decodeCueText(lines.slice(timingIndex + 1).join(' '));
		if (!text) return cues;

		cues.push({
			start: parseTimestamp(timing[1]),
			end: parseTimestamp(timing[2]),
			text
		});

		return cues;
	}, []);
};

const formatTimestamp = (time) => {
	const totalSeconds = Math.floor(time);
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;
	const minuteSeconds = `${minutes}:${String(seconds).padStart(2, '0')}`;

	return hours ? `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}` : minuteSeconds;
};

class MediaContentTranscript {
	constructor(el) {
		this.el = el;
		this.video = document.getElementById(el.dataset.videoId);
		this.list = el.querySelector('.block-media-content__video-transcript-list');
		this.status = el.querySelector('.block-media-content__video-transcript-status');
		this.cues = [];
		this.cueElements = [];

		if (!this.video || !this.list || !this.el.dataset.transcriptSrc) return;

		this.load();
	}

	async load() {
		try {
			const response = await fetch(this.el.dataset.transcriptSrc, { credentials: 'same-origin' });
			if (!response.ok) throw new Error('Transcript request failed');

			this.cues = parseWebVtt(await response.text());
			if (!this.cues.length) throw new Error('Transcript contains no cues');

			this.render();
			this.video.addEventListener('timeupdate', () => this.updateActiveCue());
			this.video.addEventListener('seeked', () => this.updateActiveCue());
		} catch (error) {
			this.status.textContent = 'Transcript unavailable.';
		}
	}

	render() {
		const fragment = document.createDocumentFragment();

		this.cues.forEach((cue) => {
			const item = document.createElement('li');
			const button = document.createElement('button');
			const text = document.createElement('span');

			item.className = 'block-media-content__video-transcript-cue';
			button.className = 'block-media-content__video-transcript-time';
			button.type = 'button';
			button.textContent = formatTimestamp(cue.start);
			button.setAttribute('aria-label', `Go to ${formatTimestamp(cue.start)} in video`);
			text.className = 'block-media-content__video-transcript-text';
			text.textContent = cue.text;

			button.addEventListener('click', () => {
				this.video.currentTime = cue.start;
				this.updateActiveCue();

				const playPromise = this.video.play();
				if (playPromise) playPromise.catch(() => {});
			});

			item.appendChild(button);
			item.appendChild(text);
			fragment.appendChild(item);
			this.cueElements.push({ item, button });
		});

		this.list.appendChild(fragment);
		this.list.hidden = false;
		this.status.remove();
	}

	updateActiveCue() {
		const currentTime = this.video.currentTime;

		this.cueElements.forEach(({ item, button }, index) => {
			const cue = this.cues[index];
			const isActive = currentTime >= cue.start && currentTime < cue.end;

			item.classList.toggle('is-active', isActive);
			if (isActive) {
				button.setAttribute('aria-current', 'true');
			} else {
				button.removeAttribute('aria-current');
			}
		});
	}
}

export default MediaContentTranscript;